import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Panel } from "../../components/ui/dashboard";

type EntityRole = "user" | "consignor" | "receiver" | "driver";
type Mode = "add" | "list" | "edit";

interface Props {
  role: EntityRole;
  mode: Mode;
  title: string;
}

// ─────────────────────────────────────────────────────────────
// Hidden defaults — built as a function so email timestamp is
// always fresh at call time, not frozen at module load.
// These are NEVER stored in form state; injected only in payload.
// ─────────────────────────────────────────────────────────────
const getHiddenDefaults = (): Record<string, string> => ({
  password: "company@135",
  email: `consignmentTest+${Date.now()}@gmail.com`,
  bank_name: "Default Bank",
  bank_account_number: "0000000000",
  bank_ifsc: "DEFB0000000",
  account_holder_name: "Account Holder",
  gst_number: "GST000000000000",
  pan_number: "PANNOTPROV",
  aadhaar_number: "000000000000",
  license_number: "LIC000000000",
  govt_id_type: "Other",
  govt_id_number: "GOVTID000000",
});

// Visible fields per role — hidden fields must NOT appear here
const fieldConfigByRole: Record<EntityRole, string[]> = {
  user: ["name", "email", "number", "address", "city", "state", "pincode"],
  consignor: ["name", "number", "address"],
  receiver: ["name", "email", "number", "address", "city", "state", "pincode"],
  driver: ["name", "email", "number", "address"],
};

// Required fields per role — only visible ones
const requiredFieldsByRole: Record<EntityRole, string[]> = {
  user: ["name", "email", "number", "address"],
  consignor: ["name", "number", "address"],
  receiver: ["name", "email", "number", "address", "city", "state", "pincode"],
  driver: ["name", "email", "number", "address"],
};

const labels: Record<string, string> = {
  name: "Full Name",
  email: "Email",
  number: "Mobile Number",
  address: "Address",
  city: "City",
  state: "State",
  pincode: "Pincode",
};

// initialForm only contains visible field keys (no hidden fields ever)
const ALL_VISIBLE_FIELDS = Array.from(
  new Set(Object.values(fieldConfigByRole).flat())
);
const initialForm = ALL_VISIBLE_FIELDS.reduce<Record<string, string>>((acc, key) => {
  acc[key] = "";
  return acc;
}, {});

const AdminEntityPage: React.FC<Props> = ({ role, mode, title }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [rows, setRows] = useState<any[]>([]);
  const [form, setForm] = useState<Record<string, string>>(initialForm);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const apiUrl = process.env.REACT_APP_API_URL;

  const fields = useMemo(() => fieldConfigByRole[role], [role]);
  const requiredFields = useMemo(() => requiredFieldsByRole[role], [role]);

  // List fetch
  useEffect(() => {
    if (!apiUrl || mode !== "list") return;
    fetch(`${apiUrl}/auth/admin/users?role=${role}`)
      .then((r) => r.json())
      .then((d) => setRows(Array.isArray(d) ? d : []))
      .catch(() => setRows([]));
  }, [apiUrl, mode, role]);

  // Edit fetch — only populate visible fields
  useEffect(() => {
    if (!apiUrl || mode !== "edit" || !id) return;
    fetch(`${apiUrl}/auth/user/${id}`)
      .then((r) => r.json())
      .then((d) => {
        setForm((prev) => {
          const next = { ...prev };
          ALL_VISIBLE_FIELDS.forEach((key) => {
            next[key] = d?.[key] ?? "";
          });
          return next;
        });
      })
      .catch(() => setMessage("Could not load record"));
  }, [apiUrl, mode, id]);

  const validate = () => {
    const newErrors: Record<string, string> = {};
    requiredFields.forEach((field) => {
      if (!form[field]?.trim()) {
        newErrors[field] = "Required";
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const onSave = async () => {
    if (!apiUrl) return;
    setMessage(null);
    if (!validate()) {
      setMessage("Please fill required fields.");
      return;
    }
    setLoading(true);

    try {
      const hiddenDefaults = getHiddenDefaults();

      // Build payload: hidden defaults first, then visible form values on top.
      // For any field that is hidden for this role (e.g. email on consignor),
      // the form value will be "" — we explicitly restore the default after.
      const payload: Record<string, string> = {
        ...hiddenDefaults,
        ...form,
        role,
      };

      // Restore hidden defaults for fields not visible in this role
      Object.keys(hiddenDefaults).forEach((key) => {
        if (!fields.includes(key) || !payload[key]?.trim()) {
          payload[key] = hiddenDefaults[key];
        }
      });

      const endpoint =
        mode === "edit" && id
          ? `${apiUrl}/auth/admin/users/${id}`
          : `${apiUrl}/auth/admin/users`;

      const res = await fetch(endpoint, {
        method: mode === "edit" ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Save failed");

      setMessage(mode === "edit" ? "Updated successfully" : "Created successfully");
      if (mode === "add") setForm(initialForm);
      if (mode === "edit") navigate(`/admin/${role}s/list`);
    } catch (e: any) {
      setMessage(e?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  // ── LIST VIEW ──────────────────────────────────────────────
  if (mode === "list") {
    return (
      <div className="p-4 md:p-6">
        <Panel
          title={`${title} - List`}
          subtitle={`Show all ${role} records with edit option`}
        >
          <div className="mb-3">
            <Link
              to={`/admin/${role}s/add`}
              className="px-3 py-2 rounded-lg text-sm bg-slate-900 text-white"
            >
              Add {title}
            </Link>
          </div>
          <div className="space-y-2">
            {rows.map((row) => (
              <div
                key={row.id}
                className="rounded-xl border border-slate-100 p-3 flex items-center justify-between"
              >
                <div>
                  <p className="text-sm font-semibold text-slate-900">{row.name}</p>
                  <p className="text-xs text-slate-500">
                    {row.email} · {row.number}
                  </p>
                </div>
                <Link
                  to={`/admin/${role}s/edit/${row.id}`}
                  className="px-2 py-1 text-xs rounded-md bg-slate-100 text-slate-700"
                >
                  Edit
                </Link>
              </div>
            ))}
          </div>
        </Panel>
      </div>
    );
  }

  // ── ADD / EDIT VIEW ────────────────────────────────────────
  return (
    <div className="p-4 md:p-6">
      <Panel
        title={`${mode === "edit" ? "Edit" : "Add"} ${title}`}
        subtitle={`Manage ${role} details`}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {fields.map((field) => (
            <div key={field} className="flex flex-col">
              <input
                className={`border rounded-lg px-3 py-2 text-sm ${
                  errors[field] ? "border-red-500" : ""
                }`}
                placeholder={
                  labels[field]
                    ? `${labels[field]}${requiredFields.includes(field) ? " *" : ""}`
                    : field
                }
                value={form[field] ?? ""}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, [field]: e.target.value }))
                }
                required={requiredFields.includes(field)}
                autoComplete="off"
              />
              {errors[field] && (
                <span className="text-xs text-red-600 mt-1">{errors[field]}</span>
              )}
            </div>
          ))}
        </div>

        {message && <p className="text-xs text-slate-600 mt-3">{message}</p>}

        <div className="mt-4 flex justify-end gap-2">
          <Link
            to={`/admin/${role}s/list`}
            className="px-3 py-2 rounded-lg text-sm bg-slate-100 text-slate-700"
          >
            Back
          </Link>
          <button
            onClick={onSave}
            disabled={loading}
            className="px-3 py-2 rounded-lg text-sm bg-indigo-600 text-white disabled:opacity-60"
          >
            {loading ? "Saving..." : mode === "edit" ? "Update" : "Create"}
          </button>
        </div>
      </Panel>
    </div>
  );
};

export default AdminEntityPage;